import React, { useState } from 'react';
import { OnboardingData, AnimalData } from './OnboardingFlow';
import {
  ANIMAL_TYPES,
  PET_TYPES,
  ACTIVITY_LEVELS,
  LIFE_STAGES,
  DOG_BREEDS,
  CAT_BREEDS,
  GOAT_BREEDS,
} from '../../../services/storeClubAPI';

interface OnboardingAnimalsProps {
  data: OnboardingData;
  updateData: (updates: Partial<OnboardingData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const OnboardingAnimals: React.FC<OnboardingAnimalsProps> = ({
  data,
  updateData,
  onNext,
  onBack,
}) => {
  const [showAddForm, setShowAddForm] = useState(data.animals.length === 0);
  const [currentAnimal, setCurrentAnimal] = useState<AnimalData>({
    animal_type: '',
    name: '',
    breed: '',
    count: 1,
    weight_lbs: undefined,
    activity_level: 'normal',
    life_stage: 'adult',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isPet = PET_TYPES.includes(currentAnimal.animal_type);

  const getBreedOptions = () => {
    if (currentAnimal.animal_type === 'dog') return DOG_BREEDS;
    if (currentAnimal.animal_type === 'cat') return CAT_BREEDS;
    if (currentAnimal.animal_type === 'goat') return GOAT_BREEDS;
    return [];
  };

  const validateAnimal = () => {
    const newErrors: Record<string, string> = {};

    if (!currentAnimal.animal_type) {
      newErrors.animal_type = 'Please select an animal type';
    }

    if (isPet && !currentAnimal.weight_lbs) {
      newErrors.weight_lbs = 'Weight is required for pets';
    }

    if (!isPet && (!currentAnimal.count || currentAnimal.count < 1)) {
      newErrors.count = 'Please enter the number of animals';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddAnimal = () => {
    if (!validateAnimal()) return;

    const newAnimals = [...data.animals, { ...currentAnimal }];
    updateData({ animals: newAnimals });
    setCurrentAnimal({
      animal_type: '',
      name: '',
      breed: '',
      count: 1,
      weight_lbs: undefined,
      activity_level: 'normal',
      life_stage: 'adult',
    });
    setShowAddForm(false);
    setErrors({});
  };

  const handleRemoveAnimal = (index: number) => {
    const newAnimals = data.animals.filter((_, i) => i !== index);
    updateData({ animals: newAnimals });
  };

  const handleNext = () => {
    if (data.animals.length === 0) {
      setErrors({ general: 'Please add at least one animal to continue' });
      return;
    }
    onNext();
  };

  const getAnimalIcon = (type: string) => {
    return ANIMAL_TYPES.find((a) => a.value === type)?.icon || '🐾';
  };

  const getAnimalLabel = (type: string) => {
    return ANIMAL_TYPES.find((a) => a.value === type)?.label || type;
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Your Animals</h2>
      <p style={styles.subtext}>
        Tell me about your animals so I can help you find the right feed and track when you're running low.
      </p>

      {data.animals.length > 0 && (
        <div style={styles.animalList}>
          {data.animals.map((animal, index) => (
            <div key={index} style={styles.animalCard}>
              <div style={styles.animalIcon}>{getAnimalIcon(animal.animal_type)}</div>
              <div style={styles.animalInfo}>
                <div style={styles.animalName}>
                  {animal.name || getAnimalLabel(animal.animal_type)}
                </div>
                <div style={styles.animalDetails}>
                  {PET_TYPES.includes(animal.animal_type)
                    ? `${animal.weight_lbs} lbs, ${animal.activity_level} activity`
                    : `${animal.count} ${getAnimalLabel(animal.animal_type).toLowerCase()}`}
                </div>
              </div>
              <button
                style={styles.removeButton}
                onClick={() => handleRemoveAnimal(index)}
                aria-label="Remove animal"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      {showAddForm ? (
        <div style={styles.addForm}>
          <h3 style={styles.formTitle}>Add an Animal</h3>

          <div style={styles.typeGrid}>
            {ANIMAL_TYPES.map((type) => (
              <button
                key={type.value}
                style={{
                  ...styles.typeButton,
                  backgroundColor: currentAnimal.animal_type === type.value ? '#508590' : '#fff',
                  color: currentAnimal.animal_type === type.value ? '#fff' : '#333',
                  borderColor: currentAnimal.animal_type === type.value ? '#508590' : '#e0e0e0',
                }}
                onClick={() => setCurrentAnimal({ ...currentAnimal, animal_type: type.value })}
              >
                <span style={styles.typeIcon}>{type.icon}</span>
                <span style={styles.typeLabel}>{type.label}</span>
              </button>
            ))}
          </div>
          {errors.animal_type && <span style={styles.error}>{errors.animal_type}</span>}

          {currentAnimal.animal_type && (
            <>
              {isPet ? (
                <>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Pet's Name (optional)</label>
                    <input
                      type="text"
                      value={currentAnimal.name || ''}
                      onChange={(e) => setCurrentAnimal({ ...currentAnimal, name: e.target.value })}
                      style={styles.input}
                      placeholder="Buddy"
                    />
                  </div>

                  {getBreedOptions().length > 0 && (
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Breed (optional)</label>
                      <select
                        value={currentAnimal.breed || ''}
                        onChange={(e) => setCurrentAnimal({ ...currentAnimal, breed: e.target.value })}
                        style={styles.select}
                      >
                        <option value="">Select breed</option>
                        {getBreedOptions().map((breed) => (
                          <option key={breed} value={breed}>{breed}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Weight (lbs)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={currentAnimal.weight_lbs || ''}
                      onChange={(e) =>
                        setCurrentAnimal({ ...currentAnimal, weight_lbs: e.target.value as any })
                      }
                      onBlur={(e) => {
                        const val = parseFloat(e.target.value);
                        if (!isNaN(val) && val >= 1 && val <= 300) {
                          setCurrentAnimal({ ...currentAnimal, weight_lbs: val });
                        } else if (e.target.value) {
                          setCurrentAnimal({ ...currentAnimal, weight_lbs: undefined });
                        }
                      }}
                      style={{
                        ...styles.input,
                        borderColor: errors.weight_lbs ? '#E73C3E' : '#e0e0e0',
                      }}
                      placeholder="50"
                    />
                    {errors.weight_lbs && <span style={styles.error}>{errors.weight_lbs}</span>}
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Activity Level</label>
                    <div style={styles.optionGroup}>
                      {ACTIVITY_LEVELS.map((level) => (
                        <button
                          key={level.value}
                          style={{
                            ...styles.optionButton,
                            backgroundColor: currentAnimal.activity_level === level.value ? '#508590' : '#fff',
                            color: currentAnimal.activity_level === level.value ? '#fff' : '#333',
                            borderColor: currentAnimal.activity_level === level.value ? '#508590' : '#e0e0e0',
                          }}
                          onClick={() => setCurrentAnimal({ ...currentAnimal, activity_level: level.value })}
                        >
                          {level.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Life Stage</label>
                    <div style={styles.optionGroup}>
                      {LIFE_STAGES.map((stage) => (
                        <button
                          key={stage.value}
                          style={{
                            ...styles.optionButton,
                            backgroundColor: currentAnimal.life_stage === stage.value ? '#508590' : '#fff',
                            color: currentAnimal.life_stage === stage.value ? '#fff' : '#333',
                            borderColor: currentAnimal.life_stage === stage.value ? '#508590' : '#e0e0e0',
                          }}
                          onClick={() => setCurrentAnimal({ ...currentAnimal, life_stage: stage.value })}
                        >
                          {stage.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      How many {getAnimalLabel(currentAnimal.animal_type).toLowerCase()} do you have?
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={currentAnimal.count || ''}
                      onChange={(e) =>
                        setCurrentAnimal({ ...currentAnimal, count: e.target.value as any })
                      }
                      onBlur={(e) => {
                        const val = parseInt(e.target.value);
                        if (!isNaN(val) && val >= 1) {
                          setCurrentAnimal({ ...currentAnimal, count: val });
                        } else if (e.target.value) {
                          setCurrentAnimal({ ...currentAnimal, count: undefined });
                        }
                      }}
                      style={{
                        ...styles.input,
                        borderColor: errors.count ? '#E73C3E' : '#e0e0e0',
                      }}
                      placeholder="12"
                    />
                    {errors.count && <span style={styles.error}>{errors.count}</span>}
                  </div>

                  {getBreedOptions().length > 0 && (
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Breed (optional)</label>
                      <select
                        value={currentAnimal.breed || ''}
                        onChange={(e) => setCurrentAnimal({ ...currentAnimal, breed: e.target.value })}
                        style={styles.select}
                      >
                        <option value="">Select breed</option>
                        {getBreedOptions().map((breed) => (
                          <option key={breed} value={breed}>{breed}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              <div style={styles.formActions}>
                <button style={styles.cancelButton} onClick={() => setShowAddForm(false)}>
                  Cancel
                </button>
                <button style={styles.addButton} onClick={handleAddAnimal}>
                  Add Animal
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <button style={styles.addAnotherButton} onClick={() => setShowAddForm(true)}>
          + Add Another Animal
        </button>
      )}

      {errors.general && (
        <div style={styles.generalError}>{errors.general}</div>
      )}

      <div style={styles.actions}>
        <button style={styles.backButton} onClick={onBack}>
          Back
        </button>
        <button style={styles.nextButton} onClick={handleNext}>
          Continue
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  heading: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
    fontFamily: "'Open Sans', sans-serif",
  },
  subtext: {
    fontSize: '0.875rem',
    color: '#666',
    margin: 0,
    lineHeight: '1.5',
  },
  animalList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  animalCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    backgroundColor: '#fff',
    padding: '1rem',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  animalIcon: {
    fontSize: '2rem',
  },
  animalInfo: {
    flex: 1,
  },
  animalName: {
    fontWeight: 600,
    color: '#333',
    fontSize: '1rem',
  },
  animalDetails: {
    fontSize: '0.875rem',
    color: '#666',
  },
  removeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#ffebee',
    color: '#E73C3E',
    fontSize: '1.5rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  },
  addForm: {
    backgroundColor: '#fff',
    padding: '1.5rem',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  formTitle: {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#333',
    margin: '0 0 1rem 0',
  },
  typeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  typeButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.75rem 0.5rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  typeIcon: {
    fontSize: '1.5rem',
  },
  typeLabel: {
    fontSize: '0.625rem',
    fontWeight: 500,
    textAlign: 'center',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
    marginBottom: '1rem',
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#333',
  },
  input: {
    padding: '0.875rem 1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    outline: 'none',
    fontFamily: "'Open Sans', sans-serif",
  },
  select: {
    padding: '0.875rem 1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
  },
  optionGroup: {
    display: 'flex',
    gap: '0.5rem',
  },
  optionButton: {
    flex: 1,
    padding: '0.75rem 0.5rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    transition: 'all 0.2s',
    fontFamily: "'Open Sans', sans-serif",
  },
  error: {
    fontSize: '0.75rem',
    color: '#E73C3E',
  },
  formActions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1rem',
  },
  cancelButton: {
    flex: 1,
    padding: '0.75rem 1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: '#666',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
  },
  addButton: {
    flex: 1,
    padding: '0.75rem 1rem',
    border: 'none',
    borderRadius: '8px',
    backgroundColor: '#508590',
    color: '#fff',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
  },
  addAnotherButton: {
    padding: '1rem',
    border: '2px dashed #508590',
    borderRadius: '12px',
    backgroundColor: 'transparent',
    color: '#508590',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
  },
  generalError: {
    backgroundColor: '#ffebee',
    color: '#E73C3E',
    padding: '0.875rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
    textAlign: 'center',
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1rem',
  },
  backButton: {
    flex: 1,
    backgroundColor: 'transparent',
    color: '#666',
    border: '2px solid #e0e0e0',
    padding: '0.875rem 1rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
  },
  nextButton: {
    flex: 2,
    backgroundColor: '#508590',
    color: '#fff',
    border: 'none',
    padding: '0.875rem 1rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Open Sans', sans-serif",
  },
};

export default OnboardingAnimals;
